"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.State = exports.MIN_ZOOMED_DATAPOINTS_HARD = void 0;
var lodash_1 = require("lodash");
var rxjs_1 = require("rxjs");
var array2d_1 = require("./data/array2d");
var data_description_1 = require("./data/data-description");
var domain_1 = require("./data/domain");
var scales_1 = require("./scales");
var utils_1 = require("./utils");
/** Avoid zooming to things like 0.4999999999999998 */
var ZOOM_EVENT_ROUNDING_PRECISION = 9;
/** Minimum zoomable width/height measured as number of columns/rows */
exports.MIN_ZOOMED_DATAPOINTS_HARD = 1;
/** Encapsulates the state of a heatmap instance.
 * Shared between the heatmap instance and all registered behaviors.
 * Also provides methods for manipulating the state
 * and takes care of emitting events related to state changes. */
var State = /** @class */function () {
  function State(dataDescription) {
    /** Data as provided to the `setData` method */
    this.originalData = data_description_1.DataDescription.empty();
    /** 2D array with the data values, for fast access to a specific row and column */
    this.dataArray = array2d_1.Array2D.empty();
    /** Values corresponding to the individual columns ("column names") */
    this.xDomain = domain_1.Domain.create([]);
    /** Values corresponding to the individual rows ("row names") */
    this.yDomain = domain_1.Domain.create([]);
    this._xAlignment = 'left';
    this._yAlignment = 'top';
    /** Extent of the data world and canvas */
    this.boxes = {
      wholeWorld: scales_1.Box.create(0, 0, 1, 1),
      visWorld: scales_1.Box.create(0, 0, 1, 1),
      canvas: scales_1.Box.create(0, 0, 1, 1)
    };
    /** Conversion between the data world and canvas coordinates */
    this.scales = (0, scales_1.Scales)(this.boxes);
    /** Custom events fired by the heatmap component, all are RxJS `BehaviorSubject` */
    this.events = {
      /** Fires when the user hovers over the component */
      hover: new rxjs_1.BehaviorSubject({
        cell: undefined,
        sourceEvent: undefined
      }),
      /** Fires when the user selects/deselects a cell (e.g. by clicking on it) */
      select: new rxjs_1.BehaviorSubject({
        cell: undefined,
        sourceEvent: undefined
      }),
      /** Fires when the user selects/deselects a region by brushing */
      brush: new rxjs_1.BehaviorSubject({
        type: 'end',
        selection: undefined,
        sourceEvent: undefined
      }),
      /** Fires when the component is zoomed in or out, or panned (translated) */
      zoom: new rxjs_1.BehaviorSubject(undefined),
      /** Fires when the window is resized. Subject value is the size of the canvas in pixels. */
      resize: new rxjs_1.BehaviorSubject(undefined),
      /** Fires when the visualized data change (including filter or domain change) */
      data: new rxjs_1.BehaviorSubject(undefined),
      /** Fires when the component is initially rendered in a div */
      render: new rxjs_1.BehaviorSubject(undefined)
    };
    this.setData(dataDescription);
  }
  Object.defineProperty(State.prototype, "xAlignment", {
    /** Controls how X axis values align with the columns when using `Heatmap.zoom` and `Heatmap.events.zoom` (position of a value on X axis can be aligned to the left edge/center/right edge of the column showing that value). Call `setAlignment` to change. */
    get: function () {
      return this._xAlignment;
    },
    enumerable: false,
    configurable: true
  });
  Object.defineProperty(State.prototype, "yAlignment", {
    /** Controls how Y axis values align with the rows when using `Heatmap.zoom` and `Heatmap.events.zoom` (position of a value on Y axis is aligned to the top edge/center/bottom edge of the row showing that value). Call `setAlignment` to change. */
    get: function () {
      return this._yAlignment;
    },
    enumerable: false,
    configurable: true
  });
  /** Replace current data by new data.
   * (If the new data are of different type, this method effectively changes the generic type parameters of `this`!
   * Returns re-typed `this`.) */
  State.prototype.setData = function (dataDescription) {
    var _a = data_description_1.DataDescription.toArray2D(dataDescription),
      array2d = _a.array2d,
      xDomain = _a.xDomain,
      yDomain = _a.yDomain;
    var self = this;
    self.originalData = dataDescription;
    self.xDomain = xDomain;
    self.yDomain = yDomain;
    self.setDataArray(array2d);
    return self;
  };
  /** Set `this.dataArray`, adjust zoom as necessary, and emit related events. */
  State.prototype.setDataArray = function (dataArray) {
    this.dataArray = dataArray;
    var newWholeWorld = scales_1.Box.create(0, 0, dataArray.nColumns, dataArray.nRows);
    var xScale = scales_1.Box.width(newWholeWorld) / scales_1.Box.width(this.boxes.wholeWorld);
    var yScale = scales_1.Box.height(newWholeWorld) / scales_1.Box.height(this.boxes.wholeWorld);
    this.boxes.wholeWorld = newWholeWorld;
    this.boxes.visWorld = scales_1.Box.clamp({
      xmin: this.boxes.visWorld.xmin * xScale,
      xmax: this.boxes.visWorld.xmax * xScale,
      ymin: this.boxes.visWorld.ymin * yScale,
      ymax: this.boxes.visWorld.ymax * yScale
    }, newWholeWorld, {
      width: exports.MIN_ZOOMED_DATAPOINTS_HARD,
      height: exports.MIN_ZOOMED_DATAPOINTS_HARD
    });
    this.scales = (0, scales_1.Scales)(this.boxes);
    this.events.data.next(undefined);
    this.emitZoom('setDataArray');
  };
  /** Return the data cell that is being pointed by the mouse in `event`.
   * Return `undefined` if there is no such cell.
   * Return a cell with `datum: undefined` if there is a cell but it is empty. */
  State.prototype.getPointedCell = function (event) {
    if (!event) {
      return undefined;
    }
    var xIndex = Math.floor(this.scales.canvasToWorld.x(event.offsetX));
    var yIndex = Math.floor(this.scales.canvasToWorld.y(event.offsetY));
    var datum = array2d_1.Array2D.get(this.dataArray, xIndex, yIndex);
    var x = this.xDomain.values[xIndex];
    var y = this.yDomain.values[yIndex];
    return {
      datum: datum,
      x: x,
      y: y,
      xIndex: xIndex,
      yIndex: yIndex
    };
  };
  /** Emit a resize event, with the current size of canvas. */
  State.prototype.emitResize = function () {
    if (!this.dom) return;
    var size = (0, utils_1.getSize)(this.dom.canvas);
    this.events.resize.next(size);
  };
  /** Emit a zoom event, based on the current zoom.
   * `origin` is an identifier of the event originator
   * (to avoid infinite loop when multiple component listen to zoom and change it). */
  State.prototype.emitZoom = function (origin) {
    var newZoom = this.getZoomEventValue(origin);
    if ((0, lodash_1.isEqual)(newZoom, this.events.zoom.value)) return; // to avoid infinite loops
    this.events.zoom.next(newZoom);
  };
  /** Controls how column/row indices and names are aligned to X and Y axes, when using `.zoom` and `.events.zoom` */
  State.prototype.setAlignment = function (x, y) {
    if (x) this._xAlignment = x;
    if (y) this._yAlignment = y;
    this.emitZoom('setAlignment');
  };
  /** Get value for zoom event (or related functions), based on the current zoom.
   * `origin` is an identifier of the event originator
   * (to avoid infinite loop when multiple component listen to zoom and change it). */
  State.prototype.getZoomEventValue = function (origin) {
    var value = this.worldBoxToBoxSelection(this.boxes.visWorld);
    if (!value) return undefined;
    return {
      xMinIndex: value.xMinIndex,
      xMaxIndex: value.xMaxIndex,
      xMin: value.xMin,
      xMax: value.xMax,
      xFirstVisibleIndex: value.xFirstIndex,
      xLastVisibleIndex: value.xLastIndex,
      xFirstVisible: value.xFirst,
      xLastVisible: value.xLast,
      yMinIndex: value.yMinIndex,
      yMaxIndex: value.yMaxIndex,
      yMin: value.yMin,
      yMax: value.yMax,
      yFirstVisibleIndex: value.yFirstIndex,
      yLastVisibleIndex: value.yLastIndex,
      yFirstVisible: value.yFirst,
      yLastVisible: value.yLast,
      origin: origin
    };
  };
  /** Convert world box to BoxSelection (i.e. richer info about the box, considering xAlignment, yAlignment) */
  State.prototype.worldBoxToBoxSelection = function (box) {
    if (!box) return undefined;
    var xMinIndex_ = (0, lodash_1.round)(box.xmin, ZOOM_EVENT_ROUNDING_PRECISION); // This only holds for xAlignment left
    var xMaxIndex_ = (0, lodash_1.round)(box.xmax, ZOOM_EVENT_ROUNDING_PRECISION); // This only holds for xAlignment left
    var xFirstIndex = (0, lodash_1.clamp)(Math.floor(xMinIndex_), 0, this.dataArray.nColumns - 1);
    var xLastIndex = (0, lodash_1.clamp)(Math.ceil(xMaxIndex_) - 1, 0, this.dataArray.nColumns - 1);
    var yMinIndex_ = (0, lodash_1.round)(box.ymin, ZOOM_EVENT_ROUNDING_PRECISION); // This only holds for yAlignment top
    var yMaxIndex_ = (0, lodash_1.round)(box.ymax, ZOOM_EVENT_ROUNDING_PRECISION); // This only holds for yAlignment top
    var yFirstIndex = (0, lodash_1.clamp)(Math.floor(yMinIndex_), 0, this.dataArray.nRows - 1);
    var yLastIndex = (0, lodash_1.clamp)(Math.ceil(yMaxIndex_) - 1, 0, this.dataArray.nRows - 1);
    var xShift = indexAlignmentShift(this.xAlignment);
    var yShift = indexAlignmentShift(this.yAlignment);
    return {
      xMinIndex: xMinIndex_ + xShift,
      xMaxIndex: xMaxIndex_ + xShift,
      xMin: domain_1.Domain.interpolateValue(this.xDomain, xMinIndex_ + xShift),
      xMax: domain_1.Domain.interpolateValue(this.xDomain, xMaxIndex_ + xShift),
      xFirstIndex: xFirstIndex,
      xLastIndex: xLastIndex,
      xFirst: this.xDomain.values[xFirstIndex],
      xLast: this.xDomain.values[xLastIndex],
      yMinIndex: yMinIndex_ + yShift,
      yMaxIndex: yMaxIndex_ + yShift,
      yMin: domain_1.Domain.interpolateValue(this.yDomain, yMinIndex_ + yShift),
      yMax: domain_1.Domain.interpolateValue(this.yDomain, yMaxIndex_ + yShift),
      yFirstIndex: yFirstIndex,
      yLastIndex: yLastIndex,
      yFirst: this.yDomain.values[yFirstIndex],
      yLast: this.yDomain.values[yLastIndex]
    };
  };
  /** Retrieve xmin/xmax/ymin/ymax (float) for visible world box from a partial ZoomEventValue `z`,
   * using `xMinIndex` or `xMin` or `xFirstVisibleIndex` or `xFirstVisible` in this order of precedence
   * (or equivalent for x/y, min/max), and adjusting for alignment shift. */
  State.prototype.getIndexFromZoomRequest = function (axis, end, z) {
    if ((0, lodash_1.isNil)(z)) return undefined;
    var fl = end === 'Min' ? 'First' : 'Last';
    var index = z["".concat(axis).concat(end, "Index")];
    var value = z["".concat(axis).concat(end)];
    var visIndex = z["".concat(axis).concat(fl, "VisibleIndex")];
    var visValue = z["".concat(axis).concat(fl, "Visible")];
    var domain = this["".concat(axis, "Domain")];
    var alignment = this["".concat(axis, "Alignment")];
    if ([index, value, visIndex, visValue].filter(function (v) {
      return !(0, lodash_1.isNil)(v);
    }).length > 1) {
      console.warn("You called zoom function with more that one of these conflicting options: ".concat(axis).concat(end, "Index, ").concat(axis).concat(end, ", ").concat(axis).concat(fl, "VisibleIndex, ").concat(axis).concat(fl, "Visible. Only the first one (in this order of precedence) will be considered."));
    }
    if (!(0, lodash_1.isNil)(index)) {
      return index - indexAlignmentShift(alignment);
    }
    if (!(0, lodash_1.isNil)(value)) {
      var interpolatedIndex = domain_1.Domain.interpolateIndex(domain, value);
      if (!(0, lodash_1.isNil)(interpolatedIndex)) {
        return interpolatedIndex - indexAlignmentShift(alignment);
      } else {
        throw new Error("".concat(axis).concat(end, " option is not applicable for zoom function, because the ").concat(axis.toUpperCase(), " domain is not numeric or not sorted. Use one of these options instead: ").concat(axis).concat(end, "Index, ").concat(axis).concat(fl, "VisibleIndex, ").concat(axis).concat(fl, "Visible."));
      }
    }
    if (!(0, lodash_1.isNil)(visIndex)) {
      if (Math.floor(visIndex) !== visIndex) throw new Error("".concat(axis).concat(fl, "VisibleIndex must be an integer, not ").concat(visIndex));
      return fl === 'First' ? visIndex : visIndex + 1;
    }
    if (!(0, lodash_1.isNil)(visValue)) {
      var foundIndex = domain.index.get(visValue);
      if (!(0, lodash_1.isNil)(foundIndex)) {
        return fl === 'First' ? foundIndex : foundIndex + 1;
      } else {
        console.warn("The provided value of ".concat(axis).concat(fl, "Visible (").concat(visValue, ") is not in the ").concat(axis.toUpperCase(), " domain."));
        return undefined;
      }
    }
    return undefined;
  };
  /** Enforce change of zoom and return the zoom value after the change */
  State.prototype.zoom = function (z) {
    var _a, _b, _c, _d;
    var visWorldBox = scales_1.Box.clamp({
      xmin: (_a = this.getIndexFromZoomRequest('x', 'Min', z)) !== null && _a !== void 0 ? _a : this.boxes.wholeWorld.xmin,
      xmax: (_b = this.getIndexFromZoomRequest('x', 'Max', z)) !== null && _b !== void 0 ? _b : this.boxes.wholeWorld.xmax,
      ymin: (_c = this.getIndexFromZoomRequest('y', 'Min', z)) !== null && _c !== void 0 ? _c : this.boxes.wholeWorld.ymin,
      ymax: (_d = this.getIndexFromZoomRequest('y', 'Max', z)) !== null && _d !== void 0 ? _d : this.boxes.wholeWorld.ymax
    }, this.boxes.wholeWorld, {
      width: exports.MIN_ZOOMED_DATAPOINTS_HARD,
      height: exports.MIN_ZOOMED_DATAPOINTS_HARD
    });
    this.zoomVisWorldBox(visWorldBox, z === null || z === void 0 ? void 0 : z.origin);
    return this.getZoomEventValue(z === null || z === void 0 ? void 0 : z.origin);
  };
  /** Set the visible world box to `visWorldBox`, update scales and emit zoom event accordingly. */
  State.prototype.zoomVisWorldBox = function (visWorldBox, origin, emit) {
    if (emit === void 0) {
      emit = true;
    }
    this.boxes.visWorld = visWorldBox;
    this.scales = (0, scales_1.Scales)(this.boxes);
    if (emit) this.emitZoom(origin);
  };
  /** Return current zoom */
  State.prototype.getZoom = function () {
    return this.getZoomEventValue(undefined);
  };
  return State;
}();
exports.State = State;
/** Return a number that has to be added world coordinates to get zoom coordinates (adjustment for column/row alignment). */
function indexAlignmentShift(alignment) {
  if (alignment === 'left' || alignment === 'top') return 0;
  if (alignment === 'center') return -0.5;
  return -1;
}